<template>
    <div class="o-text-editor" :class="{ 'o-text-editor--disabled': disabled || readonly }">
        <div v-if="!disabled && !readonly" class="o-text-editor__toolbar">
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.h1 }" :disabled="!editorReady" @click="execCommand('toggleHeading', { level: 1 })" title="Heading 1">H1</button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.h2 }" :disabled="!editorReady" @click="execCommand('toggleHeading', { level: 2 })" title="Heading 2">H2</button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.h3 }" :disabled="!editorReady" @click="execCommand('toggleHeading', { level: 3 })" title="Heading 3">H3</button>
            
            <span class="o-text-editor__separator"></span>

            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.bold }" :disabled="!editorReady" @click="execCommand('toggleBold')" title="Bold"><strong>B</strong></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.italic }" :disabled="!editorReady" @click="execCommand('toggleItalic')" title="Italic"><em>I</em></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.underline }" :disabled="!editorReady" @click="execCommand('toggleUnderline')" title="Underline"><u>U</u></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.strike }" :disabled="!editorReady" @click="execCommand('toggleStrike')" title="Strikethrough"><s>S</s></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.link }" :disabled="!editorReady" @click="setLink" title="Link"><i class="fas fa-link"></i></button>

            <span class="o-text-editor__separator"></span>

            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.alignLeft }" :disabled="!editorReady" @click="execCommand('setTextAlign', 'left')" title="Align Left"><i class="fas fa-align-left"></i></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.alignCenter }" :disabled="!editorReady" @click="execCommand('setTextAlign', 'center')" title="Align Center"><i class="fas fa-align-center"></i></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.alignRight }" :disabled="!editorReady" @click="execCommand('setTextAlign', 'right')" title="Align Right"><i class="fas fa-align-right"></i></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.alignJustify }" :disabled="!editorReady" @click="execCommand('setTextAlign', 'justify')" title="Justify"><i class="fas fa-align-justify"></i></button>

            <span class="o-text-editor__separator"></span>

            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.bulletList }" :disabled="!editorReady" @click="execCommand('toggleBulletList')" title="Bullet List"><i class="fas fa-list-ul"></i></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.orderedList }" :disabled="!editorReady" @click="execCommand('toggleOrderedList')" title="Ordered List"><i class="fas fa-list-ol"></i></button>
            <button type="button" class="o-text-editor__btn" :class="{ 'o-text-editor__btn--active': activeStates.blockquote }" :disabled="!editorReady" @click="execCommand('toggleBlockquote')" title="Blockquote"><i class="fas fa-quote-right"></i></button>

            <span class="o-text-editor__separator"></span>

            <button type="button" class="o-text-editor__btn" :disabled="!editorReady" @click="execCommand('undo')" title="Undo"><i class="fas fa-undo"></i></button>
            <button type="button" class="o-text-editor__btn" :disabled="!editorReady" @click="execCommand('redo')" title="Redo"><i class="fas fa-redo"></i></button>
        </div>

        <div ref="editorElement" class="o-text-editor__content"></div>
    </div>
</template>

<script>
import { ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { Editor } from '@tiptap/core'
// Tiptap extensions dipakai secara manual (tanpa StarterKit) karena Vue CLI limitation. //
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import Placeholder from '@tiptap/extension-placeholder'
import Heading from '@tiptap/extension-heading'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Blockquote from '@tiptap/extension-blockquote'
import History from '@tiptap/extension-history'
import Link from '@tiptap/extension-link'

export default {
    name: 'OTextEditor',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        readonly: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const editorElement = ref(null)
        const editorReady = ref(false)

        const activeStates = reactive({
            bold: false,
            italic: false,
            strike: false,
            underline: false,
            link: false,
            bulletList: false,
            orderedList: false,
            blockquote: false,
            h1: false,
            h2: false,
            h3: false,
            alignLeft: false,
            alignCenter: false,
            alignRight: false,
            alignJustify: false
        })

        let editor = null

        function updateActiveStates() {
            if (!editor) return
            activeStates.bold = editor.isActive('bold')
            activeStates.italic = editor.isActive('italic')
            activeStates.strike = editor.isActive('strike')
            activeStates.underline = editor.isActive('underline')
            activeStates.link = editor.isActive('link')
            activeStates.bulletList = editor.isActive('bulletList')
            activeStates.orderedList = editor.isActive('orderedList')
            activeStates.blockquote = editor.isActive('blockquote')
            activeStates.h1 = editor.isActive('heading', { level: 1 })
            activeStates.h2 = editor.isActive('heading', { level: 2 })
            activeStates.h3 = editor.isActive('heading', { level: 3 })
            activeStates.alignLeft = editor.isActive({ textAlign: 'left' })
            activeStates.alignCenter = editor.isActive({ textAlign: 'center' })
            activeStates.alignRight = editor.isActive({ textAlign: 'right' })
            activeStates.alignJustify = editor.isActive({ textAlign: 'justify' })
        }

        function execCommand(command, args = undefined) {
            if (!editor) return
            if (args !== undefined) {
                editor.chain().focus()[command](args).run()
            } else {
                editor.chain().focus()[command]().run()
            }
        }

        function setLink() {
            if (!editor) return
            const previousUrl = editor.getAttributes('link').href
            const url = window.prompt('URL', previousUrl)

            // cancelled
            if (url === null) {
                return
            }

            // empty
            if (url === '') {
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                return
            }

            // update link
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
        }



        onMounted(() => {
            editor = new Editor({
                element: editorElement.value,
                content: props.modelValue || '',
                editable: !props.readonly && !props.disabled,
                extensions: [
                    Document,
                    Paragraph,
                    Text,
                    Bold,
                    Italic,
                    Strike,
                    Underline,
                    Link.configure({
                        openOnClick: false,
                    }),
                    BulletList,
                    OrderedList,
                    ListItem,
                    Blockquote,
                    History,
                    Heading.configure({ levels: [1, 2, 3] }),
                    TextAlign.configure({ types: ['heading', 'paragraph'] }),

                    Placeholder.configure({
                        placeholder: props.placeholder
                    })
                ],
                onUpdate({ editor: ed }) {
                    emit('update:modelValue', ed.getHTML())
                    updateActiveStates()
                },
                onSelectionUpdate() {
                    updateActiveStates()
                },
                onTransaction() {
                    updateActiveStates()
                }
            })
            editorReady.value = true
        })

        // Sync content when modelValue changes from parent
        watch(() => props.modelValue, (newValue) => {
            if (!editor) return
            const currentHTML = editor.getHTML()
            // Avoid infinite loop: only update if content actually differs
            if (currentHTML !== newValue) {
                editor.commands.setContent(newValue || '', false)
            }
        })

        // Toggle editable state when readonly/disabled changes
        watch([() => props.readonly, () => props.disabled], ([newReadonly, newDisabled]) => {
            if (!editor) return
            editor.setEditable(!newReadonly && !newDisabled)
        })

        // Destroy editor on unmount to prevent memory leaks
        onBeforeUnmount(() => {
            if (editor) {
                editor.destroy()
                editor = null
                editorReady.value = false
            }
        })

        return {
            editorElement,
            editorReady,
            activeStates,
            execCommand,
            setLink
        }
    }
}
</script>

<style scoped>
.o-text-editor {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.o-text-editor:focus-within {
    border-color: var(--primary-color, #f27178);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(242, 113, 120, 0.15));
}

.o-text-editor--disabled {
    opacity: 0.6;
    pointer-events: none;
    background: #f9fafb;
}

/* Toolbar */
.o-text-editor__toolbar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 10px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    flex-wrap: wrap;
}

.o-text-editor__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #6b7280;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.o-text-editor__btn:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #e5e7eb;
    color: #374151;
}

.o-text-editor__btn--active {
    background: var(--primary-color, #f27178);
    border-color: var(--primary-color, #f27178);
    color: #fff;
}

.o-text-editor__btn--active:hover:not(:disabled) {
    background: var(--primary-color, #f27178);
    border-color: var(--primary-color, #f27178);
    filter: brightness(0.9);
    color: #fff;
}

.o-text-editor__btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.o-text-editor__separator {
    display: inline-block;
    width: 1px;
    height: 18px;
    background: #d1d5db;
    margin: 0 6px;
}

/* Editor Content Area */
.o-text-editor__content {
    min-height: 220px;
}

.o-text-editor__content :deep(.ProseMirror) {
    padding: 16px 20px;
    min-height: 220px;
    outline: none;
    border: none;
    box-shadow: none;
    font-size: 0.9rem;
    line-height: 1.7;
    color: #1f2937;
}

.o-text-editor__content :deep(.ProseMirror:focus) {
    outline: none;
    border: none;
    box-shadow: none;
}

.o-text-editor__content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    color: #9ca3af;
    pointer-events: none;
    height: 0;
    font-style: italic;
}

.o-text-editor__content :deep(.ProseMirror ul),
.o-text-editor__content :deep(.ProseMirror ol) {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
}

.o-text-editor__content :deep(.ProseMirror li) {
    margin-bottom: 0.25rem;
}

.o-text-editor__content :deep(.ProseMirror p) {
    margin: 0 0 0.5rem 0;
}

.o-text-editor__content :deep(.ProseMirror h1),
.o-text-editor__content :deep(.ProseMirror h2),
.o-text-editor__content :deep(.ProseMirror h3) {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
    line-height: 1.3;
}

.o-text-editor__content :deep(.ProseMirror h1) {
    font-size: 1.5rem;
}

.o-text-editor__content :deep(.ProseMirror h2) {
    font-size: 1.25rem;
}

.o-text-editor__content :deep(.ProseMirror h3) {
    font-size: 1.1rem;
}

.o-text-editor__content :deep(.ProseMirror blockquote) {
    border-left: 3px solid #d1d5db;
    padding-left: 1rem;
    margin-left: 0;
    margin-right: 0;
    color: #4b5563;
    font-style: italic;
}



.o-text-editor__content :deep(.ProseMirror p:last-child) {
    margin-bottom: 0;
}

.o-text-editor__content :deep(.ProseMirror strong) {
    font-weight: 600;
}
</style>
